import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import ImportExportBar from "@/components/ImportExportBar";
import { PaginationBar, usePagination } from "@/components/Pagination";

const empty = { company_code: "", name: "", description: "" };

export default function CompanyMaster() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);

  const load = () => api.get("/companies").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { company_code: f.company_code || null, name: f.name, description: f.description || null };
      if (editId) await api.patch(`/companies/${editId}`, payload);
      else await api.post("/companies", payload);
      toast.success(editId ? "Company updated" : "Company added");
      setF(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (c) => {
    setF({ company_code: c.company_code || "", name: c.name, description: c.description || "" });
    setEditId(c.id); setOpen(true);
  };

  const del = async (c) => {
    if (!confirm(`Delete company ${c.company_code || ""} — ${c.name}? This cannot be undone.`)) return;
    try { await api.delete(`/companies/${c.id}`); toast.success("Company deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Building2 className="w-3 h-3"/> Inventory · Masters</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Company Master</h1>
        </div>
        <div className="flex items-center gap-2">
          <ImportExportBar masterKey="companies" filters={{}} onImported={load} />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-company-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
                <Plus className="w-4 h-4 mr-1"/> New Company
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card border-border max-w-lg">
              <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Company</DialogTitle></DialogHeader>
              <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Company Code (blank = auto-generated)</Label>
                  <Input data-testid="company-code" value={f.company_code} onChange={(e) => setF(x => ({ ...x, company_code: e.target.value.toUpperCase() }))} className="mt-1 rounded-sm bg-secondary border-border font-mono uppercase" placeholder="e.g. CMP-HONDA" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Company Name *</Label>
                  <Input data-testid="company-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. Honda" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label>
                  <Input data-testid="company-desc" value={f.description} onChange={(e) => setF(x => ({ ...x, description: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                </div>
                <Button data-testid="save-company-btn" onClick={save} disabled={!f.name} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                  {editId ? "Update" : "Save"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Company Code</th>
              <th className="text-left p-3">Company Name</th>
              <th className="text-left p-3">Description</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No companies yet</td></tr>}
            {pg.pageItems.map(c => (
              <tr key={c.id} data-testid={`company-row-${c.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary">{c.company_code || "-"}</td>
                <td className="p-3">{c.name}</td>
                <td className="p-3 text-xs text-muted-foreground">{c.description || "-"}</td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <Button data-testid={`edit-company-${c.id}`} size="sm" variant="ghost" onClick={() => edit(c)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`del-company-${c.id}`} size="sm" variant="ghost" onClick={() => del(c)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
